import React, {useContext, useEffect} from "react";
import {Outlet, Link, useNavigate, useLocation} from "react-router-dom";
import {Helmet} from "react-helmet-async";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import BreadcrumbInterface from "../../interfaces/core/BreadcrumbInterface";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {CustomerInterface} from "../../interfaces/customer/CustomerInterface";
import {BagPlus, BoxArrowRight, ChatDots, Download, Icon, ListUl, Person} from "react-bootstrap-icons";

const ASSISTANT_CUSTOMER_IDS = [214167960];

// Заголовок вкладки по разделу кабинета
function accountTitle(pathname: string): string {
    const order = pathname.match(/^\/account\/order\/([^/]+)/);
    if (order) return `Заказ №${order[1]} — Личный кабинет`;
    const titles: Record<string, string> = {
        '/account': 'Мои данные',
        '/account/profile': 'Мои данные',
        '/account/order-list': 'Мои заказы',
        '/account/assistant': 'Консультант',
    };
    const title = titles[pathname.replace(/\/$/, '')];
    return title ? `${title} — Личный кабинет` : 'Личный кабинет';
}

// Активный раздел: /account открывает «Мои данные», страница заказа относится к «Моим заказам»
function isActiveLink(pathname: string, link: string): boolean {
    const path = pathname.replace(/\/$/, '');
    if (link === '/account/profile') return path === '/account' || path === link;
    if (link === '/account/order-list') return path === link || path.startsWith('/account/order/');
    return path === link;
}

interface MenuItem {
    name: string,
    icon: Icon,
    link: string,
    type: 'link' | 'download',
}

export default function Layout() {
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);

    const navigate = useNavigate();
    const location = useLocation();

    const customer = state.customer as CustomerInterface;

    const menu: MenuItem[] = [
        {name: 'Мои данные', icon: Person, link: '/account/profile', type: 'link'},
        {name: 'Новый заказ', icon: BagPlus, link: '/basket', type: 'link'},
        {name: 'Мои заказы', icon: ListUl, link: '/account/order-list', type: 'link'},
        // Консультант пока в тесте — ссылка только у этих клиентов, страница по прямому адресу открывается у всех
        ...(ASSISTANT_CUSTOMER_IDS.includes(customer?.id) ? [{name: 'Консультант', icon: ChatDots, link: '/account/assistant', type: 'link'} as MenuItem] : []),
        {name: 'Скачать прайс-лист', icon: Download, link: '/api/srz/order/download-excel-price', type: 'download'},
    ]


    useEffect(() => {
        let breadcrumbs: BreadcrumbInterface[] = [];

        breadcrumbs.push({
            name: 'Личный кабинет',
        })
        dispatch({type: ReducerActionType.SET_BREADCRUMBS, payload: breadcrumbs})
    }, []);

    const logout = () => {
        dispatch({ type: 'SET_MANAGER', payload: {manager: null, customer: null} });
        navigate('/')
    }


    return (<div className="container">
        <Helmet><title>{accountTitle(location.pathname)}</title></Helmet>
        {/* Мобильное меню — плитки с иконками */}
        <nav className="d-lg-none mb-4 account-tiles">
            {menu.map((item, index) => {
                const ItemIcon = item.icon;
                const isActive = item.type === 'link' && isActiveLink(location.pathname, item.link);
                const content = <>
                    <ItemIcon size={22} className="account-tiles__icon"/>
                    <span>{item.type === 'download' ? 'Прайс-лист' : item.name}</span>
                </>;

                return item.type === 'download'
                    ? <a key={index} href={item.link} className="account-tiles__item" download>{content}</a>
                    : <Link key={index} to={item.link} className={`account-tiles__item ${isActive ? 'active' : ''}`}>{content}</Link>;
            })}
            <button type="button" className="account-tiles__item" onClick={logout}>
                <BoxArrowRight size={22} className="account-tiles__icon"/>
                <span>Выход</span>
            </button>
        </nav>

        <div className={'gx-3 gx-md-6 row gy-7  mb-5'}>
            {/* Вертикальное меню для десктопа */}
            <div className="col-0 col-lg-3 d-none d-lg-block position-relative">
                <div className="filter rounded-4">
                    <nav className="nav flex-column pb-4 pt-5">
                        {menu.map((item, index) => {
                            const ItemIcon = item.icon;
                            const isActive = item.type === 'link' && isActiveLink(location.pathname, item.link);
                            const className = `nav-link d-flex align-items-center pb-3 px-3 rounded-2 mb-2 text-dark text-decoration-none account-menu__item ${
                                isActive ? 'active' : ''
                            }`;

                            if (item.type === 'download') {
                                return (
                                    <a key={index} href={item.link} className={className} download>
                                        <ItemIcon className="me-3 text-orange"/>
                                        {item.name}
                                    </a>
                                );
                            }

                            return (
                                <Link key={index} to={item.link} className={className}>
                                    <ItemIcon className="me-3 text-orange"/>
                                    {item.name}
                                </Link>
                            );
                        })}
                        <span
                            className="pointer nav-link d-flex align-items-center pb-3 px-3 rounded-2 mb-2 text-decoration-none text-dark"
                            onClick={logout}

                        >
                            <BoxArrowRight className="me-3 text-orange"/>
                            <b>Выход</b>
                        </span>
                        <span className=" nav-link d-flex" >
                            {customer?.accounts?.filter((account) => account.manager).map((account, index) => (
                                <div key={index} className={'bg-white p-4 w-100 rounded-2'}>
                                    <div className={'text-muted'}><small>Ваш персональный менеджер</small></div>
                                    <h4 ><b>{account.manager.name}</b></h4>
                                    <div>
                                        <a href={`mailto:${account.manager.email}`}>
                                            {account.manager.email}
                                        </a>
                                    </div>
                                    <div className={'mt-2'}>
                                        {account.manager.phone_additional}
                                    </div>
                                </div>
                            ))}

                        </span>
                    </nav>
                </div>



            </div>
            <div className="col-12 col-lg-9">
                <Outlet/>
            </div>
        </div>
    </div>);
}